import { Flex, Tooltip } from '@chakra-ui/react';
import { useStableCallback } from '@main/shared/browser';
import { NAMESPACE } from '@main/shared/utils';
import { ColumnFiltersState, SortingState, Table } from '@tanstack/react-table';
import useLocalStorageState from 'ahooks/es/useLocalStorageState';
import equal from 'fast-deep-equal';
import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  emptyLegacyColumnIdMap,
  getLegacyColumnIdMap,
  LegacyColumnIdMap,
  resolveLegacyColumnIdEntries,
} from '../legacy-column-ids';
import { TableButton } from '../shared/table-menu';

type FiltersAndSortingConfiguration = {
  filters: ColumnFiltersState;
  sorting: SortingState;
};

type Configurations = Record<string, FiltersAndSortingConfiguration>;

const hasConfiguration = (configuration: FiltersAndSortingConfiguration) =>
  configuration.filters.length !== 0 || configuration.sorting.length !== 0;

const defaultConfiguration: FiltersAndSortingConfiguration = { filters: [], sorting: [] };

const resolveLegacyColumnIds = (
  configuration: FiltersAndSortingConfiguration,
  legacyColumnIdMap: LegacyColumnIdMap,
): FiltersAndSortingConfiguration => {
  const filters = resolveLegacyColumnIdEntries(configuration.filters, legacyColumnIdMap);
  const sorting = resolveLegacyColumnIdEntries(configuration.sorting, legacyColumnIdMap);
  return filters === configuration.filters && sorting === configuration.sorting
    ? configuration
    : { ...configuration, filters, sorting };
};

const useSavedConfigurations = (
  tableName: string,
  settingsNamespace: string | undefined,
  tableId?: string,
) => {
  const key = useMemo(
    () =>
      NAMESPACE +
      [tableName, tableId, settingsNamespace, 'filters-and-sorting'].filter(Boolean).join(':'),
    [tableName, tableId, settingsNamespace],
  );

  return useLocalStorageState<Configurations>(key, {
    defaultValue: {},
  });
};

const useSavedConfiguration = (
  tableName: string,
  settingsNamespace: string | undefined,
  tableId?: string,
) => {
  const defaultId = 'default';
  const [configurations, setConfigurations] = useSavedConfigurations(
    tableName,
    settingsNamespace,
    tableId,
  );

  return [
    configurations?.[defaultId] ?? defaultConfiguration,
    useStableCallback((configuration: FiltersAndSortingConfiguration) =>
      setConfigurations((prevState) => ({ ...prevState, [defaultId]: configuration })),
    ),
  ] as const;
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const useCurrentConfiguration = <TData extends unknown>(table: Table<TData>) => {
  const state = table.getState();
  const filters = state.columnFilters;
  const sorting = state.sorting;
  return useMemo(() => ({ filters, sorting }), [filters, sorting]);
};
const removeConfigs = <T extends { id: string }>(configs: T[], toRemove: T[]) => {
  const idsToRemove = toRemove.map(({ id }) => id);
  return configs.filter(({ id }) => !idsToRemove.includes(id));
};
const isNonEmptyConfiguration = (
  config?: FiltersAndSortingConfiguration,
): config is FiltersAndSortingConfiguration =>
  !!config && (config.filters.length !== 0 || config.sorting.length !== 0);

export type SaveConfigurationProps<TData> = {
  table: Table<TData>;
  tableName: string;
  settingsNamespace?: string;
  tableId?: string;
  isColumnsLoading: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const SaveConfiguration = <TData extends unknown>({
  table,
  tableName,
  settingsNamespace,
  tableId,
  isColumnsLoading,
}: SaveConfigurationProps<TData>) => {
  const { t } = useTranslation('ui');

  const [savedConfiguration, setSavedConfiguration] = useSavedConfiguration(
    tableName,
    settingsNamespace,
    tableId,
  );
  const allColumns = table.getAllColumns();
  const legacyColumnIdMap = useMemo(
    () =>
      isColumnsLoading
        ? emptyLegacyColumnIdMap
        : getLegacyColumnIdMap(allColumns.map((column) => column.columnDef)),
    [allColumns, isColumnsLoading],
  );
  const resolvedSavedConfiguration = useMemo(
    () => resolveLegacyColumnIds(savedConfiguration, legacyColumnIdMap),
    [savedConfiguration, legacyColumnIdMap],
  );
  useEffect(() => {
    if (resolvedSavedConfiguration !== savedConfiguration) {
      setSavedConfiguration(resolvedSavedConfiguration);
    }
  }, [resolvedSavedConfiguration, setSavedConfiguration, savedConfiguration]);

  const currentConfiguration = useCurrentConfiguration(table);
  const hasSavedConfiguration =
    resolvedSavedConfiguration && hasConfiguration(resolvedSavedConfiguration);
  const hasChanges = !equal(currentConfiguration, resolvedSavedConfiguration);
  const reset = useStableCallback(() => {
    table.setColumnFilters(resolvedSavedConfiguration?.filters ?? []);
    table.setSorting(resolvedSavedConfiguration?.sorting ?? []);
  });
  const clear = useStableCallback(() => {
    table.setColumnFilters([]);
    table.setSorting([]);
  });
  const propagateUp = useStableCallback((newConfiguration: FiltersAndSortingConfiguration) => {
    table.setColumnFilters(newConfiguration.filters);
    table.setSorting(newConfiguration.sorting);
  });
  const isCurrentConfigNonEmpty = isNonEmptyConfiguration(currentConfiguration);
  const isSavedConfigNonEmpty = isNonEmptyConfiguration(resolvedSavedConfiguration);

  const needsPropagate = !isCurrentConfigNonEmpty && isSavedConfigNonEmpty;

  const wasColumnsLoading = useDeferredValue(isColumnsLoading);
  const [firstRender, setFirstRender] = useState(true);
  useEffect(() => setFirstRender(false), []);
  useEffect(() => {
    const finishedLoading = wasColumnsLoading && !isColumnsLoading;
    const firstRenderNotLoading = firstRender && !isColumnsLoading;
    const shouldPropagate = finishedLoading || firstRenderNotLoading;
    if (needsPropagate && shouldPropagate) {
      propagateUp(resolvedSavedConfiguration);
    }
  }, [
    firstRender,
    isColumnsLoading,
    needsPropagate,
    propagateUp,
    resolvedSavedConfiguration,
    wasColumnsLoading,
  ]);

  const columnIds = allColumns.map((column) => column.columnDef.id);
  // We need to deal with columns changing, e.g custom fields being added or removed.
  useEffect(() => {
    if (isColumnsLoading) {
      return;
    }
    const nonExistentFilters = resolvedSavedConfiguration?.filters.filter(
      ({ id }) => !columnIds.includes(id),
    );
    const nonExistentSorts = resolvedSavedConfiguration?.sorting.filter(
      ({ id }) => !columnIds.includes(id),
    );

    if (nonExistentFilters?.length || nonExistentSorts?.length) {
      const newConfiguration = {
        ...resolvedSavedConfiguration,
        filters: removeConfigs(resolvedSavedConfiguration?.filters ?? [], nonExistentFilters ?? []),
        sorting: removeConfigs(resolvedSavedConfiguration?.sorting ?? [], nonExistentSorts ?? []),
      };
      setSavedConfiguration(newConfiguration);
      propagateUp(newConfiguration);
    }
  }, [columnIds, isColumnsLoading, propagateUp, resolvedSavedConfiguration, setSavedConfiguration]);

  return hasChanges || hasSavedConfiguration ? (
    <Flex gap={2}>
      <Tooltip label={t('table.saveConfiguration.noChangesTooltip')} isDisabled={hasChanges}>
        <TableButton
          onClick={() => setSavedConfiguration(currentConfiguration)}
          isDisabled={!hasChanges}
          variant="outline"
        >
          {t('table.saveConfiguration.save')}
        </TableButton>
      </Tooltip>
      {hasChanges && hasSavedConfiguration ? (
        <TableButton variant="ghost" onClick={reset}>
          {t('table.saveConfiguration.reset')}
        </TableButton>
      ) : (
        <TableButton variant="ghost" onClick={clear}>
          {t('table.saveConfiguration.clear')}
        </TableButton>
      )}
    </Flex>
  ) : null;
};
